<template>
    <div class="office" v-show="isLoaded">
        <div class="top" v-if="isLoaded">
            <div class="ls">
                <back-button />
                <input v-if="!fatalError" type="text" class="name" v-model="title" />
            </div>
            <div v-if="!fatalError" class="rs">
                <template v-if="mode !== 'review' || !hasUnsavedChanges">
                    <div v-if="mode === 'review' && !pkg.is_public" class="status">
                        <status-chooser :value="pkg.status" :disabled="true" />
                    </div>
                    <div v-if="mode !== 'edit'" class="assigned-to">
                        <span v-if="mode === 'send'" class="info-label desc">Deliver To: </span>
                        <user-chooser
                            placeholder="Unassigned"
                            noSelectionText="Unassigned"
                            :value="assignedToValue"
                            @change="onAssignedToChange"
                        />
                    </div>
                </template>
                <office-main-buttons :mode="mode" />
            </div>
        </div>
        <!-- key attr fixes Vue bug https://github.com/vuejs/vue/issues/9054 -->
        <div class="main-wrapper" key="office-main">
            <div class="left" :style="leftBarStyles" v-if="isLoaded">
                <div class="title">Forms & Attachments</div>
                <draggable v-model="sequence" @sort="(e) => scrollToItem(e.newIndex)">
                    <div class="item form" v-for="(seq, index) in sequence" :key="index" @click="scrollToItem(index)">
                        <span class="far" :class="seq.type === 'form' ? 'fa-file' : 'fa-paperclip'"></span>
                        <div class="text">
                            {{ seq.type === 'form' ? formById(seq.id).name : attachment(seq.id).name }}
                        </div>
                        <span class="far fa-times" @click.stop="removeItem(index)" title="Remove item"></span>
                    </div>
                    <div v-if="!sequence.length" class="info-label">No Items</div>
                </draggable>

                <div class="item extra" @click="openLibrary">
                    <span class="far fa-plus-circle"></span>
                    <div class="text">Add a form</div>
                </div>
                <div class="item extra" @click="addAttachment">
                    <span class="far fa-paperclip"></span>
                    <div class="text">Add/Request an attachment</div>
                </div>
                <template v-if="!pkg.is_public && (mode == 'review' || mode === 'edit')">
                    <div class="title">History</div>
                    <history-items :mode="mode" />
                </template>
                <template v-if="mode !== 'review' && mode !== 'edit'">
                    <div class="title">Sending To</div>
                    <div class="info-label">Internal note to keep track of this form's recipient</div>
                    <input type="text" v-model="sendingTo" />
                </template>
            </div>

            <div class="canvas-wrapper" :style="canvasWrapperStyles" v-show="sequence.length">
                <filler-nav @show-qr-code="showQrCode" />
                <filler-canvas
                    mode="office"
                    @loaded="onFillerLoad"
                    @error="onFillerError"
                    :pkgId="$route.params.pkgId"
                    ref="fillerCanvas"
                />
            </div>

            <div v-show="!sequence.length" class="no-items">
                <div class="notice">
                    <div>Nothing in this package.</div>
                    <div class="button-group">
                        <button @click="openLibrary">Add Form</button>
                        <button @click="addAttachment">Request Attachment</button>
                    </div>
                </div>
            </div>

            <div v-show="deviceLink" class="qr-overlay">
                <div>
                    <h4>Fill This Form on Another Device</h4>
                    <div class="qr-code">
                        <canvas ref="qrCanvas" width="200" height="200"></canvas>
                    </div>
                    <button class="primary" @click="hideQrCode">Finished on Other Device</button>
                </div>
            </div>
        </div>

        <form-signature />

        <div v-if="fatalError" class="error-overlay">
            <i class="far fa-exclamation-circle"></i>
            <div>{{ fatalError }}</div>
        </div>
    </div>
</template>
<script>
    import { mapGetters } from 'vuex';
    import { pixelate, capitalize } from '../utils/strings';
    import draggable from 'vuedraggable';
    import OfficeMainButtons from '../components/office/OfficeMainButtons';
    import FillerNav from '../components/filler/FillerNav';
    import FillerCanvas from '../components/filler/FillerCanvas';
    import FormSignature from '../components/form/FormSignature';
    import HistoryItems from '../components/office/HistoryItems';
    import UserChooser from '../components/UserChooser';
    import StatusChooser from '../components/StatusChooser';
    import QrCreator from 'qr-creator';
    import * as Sentry from '@sentry/browser';

    export default {
        name: 'Office',
        data() {
            return {
                fatalError: '',
                deviceLink: false,
            };
        },
        components: {
            FillerCanvas,
            FillerNav,
            OfficeMainButtons,
            draggable,
            FormSignature,
            HistoryItems,
            UserChooser,
            StatusChooser,
        },
        computed: {
            ...mapGetters({
                user: 'user/user',
                users: 'org/users',
                accessorsByBoxId: 'boxes/accessorsByBoxId',
                dimensions: 'layout/dimensions',
                formById: 'form/formById',
                boxById: 'boxes/boxById',
                parseBoxString: 'boxes/parseBoxString',
                attachment: 'package/attachment',
                assignedToUserId: 'office/assignedToUserId',
                assignedToBoxId: 'office/assignedToBoxId',
                forms: 'form/forms',
                pkg: 'package/pkg',
                isLoaded: 'filler/isLoaded',
                pkgLink: 'package/link',
                officeHasUnsavedChanges: 'office/hasUnsavedChanges',
                fillerHasUnsavedChanges: 'filler/hasUnsavedChanges',
                contactInfo: 'package/getContactInfo',
                appName: 'base/appName',
            }),
            mode() {
                return this.$route.params.mode;
            },
            hasUnsavedChanges() {
                return this.officeHasUnsavedChanges || this.fillerHasUnsavedChanges;
            },
            leftBarStyles() {
                return {
                    width: pixelate(this.dimensions.widthLeftBar),
                };
            },
            canvasWrapperStyles() {
                return {
                    width: `calc(100% - ${pixelate(this.dimensions.widthLeftBar)})`,
                };
            },
            title: {
                get() {
                    return this.$store.getters['office/title'];
                },
                set(value) {
                    this.$store.commit('office/title', value);
                },
            },
            sendingTo: {
                get() {
                    return this.$store.getters['office/sendingTo'];
                },
                set(sendingTo) {
                    this.$store.commit('office/sendingTo', sendingTo);
                },
            },
            sequence: {
                get() {
                    return this.$store.getters['package/sequence'];
                },
                set(sequence) {
                    this.$store.commit('package/sequence', sequence);
                },
            },
            assignedToValue() {
                return this.assignedToBoxId ? 'box_' + this.assignedToBoxId : this.assignedToUserId;
            },
            isCurrentUserAnAssignee() {
                if (this.user.id === this.assignedToUserId) return true;
                if (this.assignedToBoxId && this.accessorsByBoxId(this.assignedToBoxId).includes(this.user.id)) return true;

                return false;
            },
            isCurrentUserARecipient() {
                if (this.user.id === this.sendingToUserId) return true;
                if (this.sendingToBoxId && this.accessorsByBoxId(this.sendingToBoxId).includes(this.user.id)) return true;

                return false;
            },
        },
        methods: {
            openLibrary() {
                let disableForms = [];
                for (let seq of this.sequence) {
                    if (seq.type !== 'form') continue;
                    let formId;
                    if (/^new/.test(seq.id)) {
                        formId = seq.id.substr(3);
                    } else {
                        formId = this.pkg.form_instances[seq.id].form_template_id;
                    }
                    disableForms.push(formId);
                }
                this.$store.dispatch('dialog/set', {
                    type: 'info',
                    componentName: 'library-main',
                    bodyComponentProps: {
                        readOnly: true,
                        isDialog: true,
                        disableForms,
                    },
                    dialogProps: {
                        showFooter: false,
                        showX: true,
                        width: '100%',
                        height: '100%',
                        innerPadding: '0',
                    },
                    listeners: {
                        itemsSelected: async (items) => {
                            if (items) {
                                let ids = items.reduce((acc, item) => {
                                    if (item.form_template_id) acc.push(item.form_template_id);
                                    return acc;
                                }, []);

                                if (ids) {
                                    await this.$store.dispatch('office/addForms', ids);
                                }
                            }
                            this.$store.dispatch('dialog/clear');
                        },
                    },
                });
            },
            addAttachment() {
                this.$store.dispatch('office/addAttachment');
            },
            scrollToItem(index) {
                this.$store.commit('filler/scrolledToItem', index);
            },
            async removeItem(index) {
                let type = this.sequence[index].type;
                let confirmed = await this.$store.dispatch('dialog/set', {
                    type: 'confirm',
                    header: `Remove ${type[0].toUpperCase() + type.substr(1)}?`,
                    critical: true,
                    body: `Are you sure you want to remove this ${type}? This cannot be undone.`,
                });

                if (confirmed) this.$store.dispatch('office/removeItem', index);
            },
            async onAssignedToChange(assigneeStr) {
                let assigneeDetails = this.parseBoxString(assigneeStr);

                if (this.mode !== 'review') {
                    if (!assigneeDetails) {
                        this.$store.commit(`office/assignedToUserId`, '');
                        this.$store.commit(`office/assignedToBoxId`, '');
                        return;
                    }

                    this.$store.commit(`office/assignedTo${capitalize(assigneeDetails.type)}Id`, assigneeDetails.id);
                    return;
                }

                let change = await this.$store.dispatch('package/reassignConfirm', assigneeStr);
                let assignMessage = '';
                if (typeof change === 'string') {
                    assignMessage = change;
                    change = true;
                }

                if (change) {
                    let updates = { assignedToUserId: '', assignedToBoxId: '' };
                    if (assigneeDetails) {
                        updates[`assignedTo${capitalize(assigneeDetails.type)}Id`] = assigneeDetails.id;
                    }
                    this.$store.commit('office/init', updates);
                    await this.$store.dispatch('package/update', {
                        pkgId: this.pkg.id,
                        updates: { ...updates, assignMessage },
                    });
                }
            },
            async onFillerLoad() {
                // If we somehow got here with an inappropriate office mode, change it to the correct one
                if (this.mode === 'send' && this.pkg.status !== 'Draft') {
                    Sentry.captureMessage(`Attempted to open package ${this.pkg.id} with ${this.pkg.status} status in office send mode`, 'warning');
                    let mode = 'review';
                    if (this.pkg.status === 'Pending' && this.pkg.revision.sending_to_user_id === this.user.id) {
                        mode = 'edit';
                    }
                    this.$store.dispatch('office/changeMode', mode);
                }

                this.$store.commit('office/init', {
                    sendingTo: this.pkg.revision.sending_to,
                    sendingToUserId: this.pkg.revision.sending_to_user_id,
                    sendingToBoxId: this.pkg.revision.sending_to_box_id,
                    sendingToContactId: this.pkg.revision.sending_to_contact_id,
                    sendingTo: this.pkg.revision.sending_to,
                    title: this.pkg.title,
                    assignedToUserId: this.pkg.assigned_to_user_id || '',
                    assignedToBoxId: this.pkg.assigned_to_box_id || '',
                    contactId: this.pkg.contactId || '',
                });

                this.$store.dispatch('package/loadHistory');
                this.$store.dispatch('package/loadPayments');

                let noAssignees = !this.assignedToUserId && !this.assignedToBoxId;
                let markReadFromReviewed = this.mode === 'review' && (noAssignees || this.isCurrentUserAnAssignee);
                let markReadFromEdit = this.mode === 'edit' && this.isCurrentUserARecipient;

                if (!this.pkg.is_read && (markReadFromReviewed || markReadFromEdit)) {
                    this.$store.dispatch('package/update', {
                        updates: { isRead: true },
                        pkgId: this.pkg.id,
                    });
                }

                if (this.pkg.device_token) {
                    this.showQrCode();
                }
            },
            async onFillerError(e) {
                this.fatalError = e.message;
                if (e.code === 'PKG-UNP') {
                    // Show who the current assignee for this package is
                    let res = await this.$api.get(`packages/assigned/${this.$route.params.pkgId}`);
                    let assigneeName = res.type === 'user' ? this.users[res.id].name : this.boxById(res.id).name;
                    this.fatalError = `${e.message}. ${assigneeName} is the current assignee.`;
                }
            },
            async showQrCode() {
                await this.$store.dispatch('package/save', 'save');
                this.deviceLink = await this.$store.dispatch('package/getDeviceLink');
                if (process.env.NODE_ENV === 'development') {
                    console.info('Device link:', this.deviceLink);
                }
                QrCreator.render(
                    {
                        text: this.deviceLink,
                        radius: 0,
                        ecLevel: 'H',
                        size: 300,
                    },
                    this.$refs.qrCanvas
                );
            },
            async hideQrCode() {
                await this.$store.dispatch('package/deleteDeviceLink');
                this.deviceLink = false;
                this.$loading(true);
                await this.$store.dispatch('filler/load', { id: this.pkg.id });
                this.$loading(false);
            },
        },
        created() {
            this.$store.commit('filler/isLoaded', false);
            this.$store.commit('filler/isOffice', true);
        },
        async mounted() {
            this.$store.commit('layout/widthLeftBar', 300);
            this.$store.commit('layout/widthRightBar', 0);
            document.title = `${capitalize(this.mode)} Package - ${this.appName}`;
        },
        async beforeRouteLeave(to, from, next) {
            if (this.hasUnsavedChanges) {
                let leave = await this.$store.dispatch('dialog/set', {
                    type: 'confirm',
                    header: 'Unsaved Changes',
                    body: 'Leave with unsaved changes?',
                    critical: true,
                    okText: 'Leave',
                    cancelText: 'Cancel',
                });
                if (!leave) return;
            }
            this.$loading();

            // form must be cleared before leave, because this hook will be called again (and the unsaved changes popup hidden behind loading screen) if navigation is redirected by middleware
            this.$store.commit('office/init');
            this.$store.commit('filler/clearFormData');
            this.$store.commit('filler/hasUnsavedChanges', false);
            this.$store.commit('filler/invalidIds', []);
            next();
        },
    };
</script>

<style lang="scss" scoped>
    @import '@/scss/forms.scss';

    .office {
        height: 100%;
    }

    .top {
        height: $height-office-header;
        border-bottom: 1px solid $color-border;
        display: flex;
        justify-content: space-between;
        padding: 0 1em;
        z-index: 1006;
        position: relative;
        background: $color-white;

        > div,
        .main-buttons,
        .status,
        .assigned-to {
            display: flex;
            align-items: center;
            margin-left: 1em;
        }

        .assigned-to .desc {
            margin-right: 0.25em;
        }

        .ls {
            margin: 0;
        }

        .ls,
        .ls .name {
            flex-grow: 1;
        }

        .name {
            font-size: $font-size-larger;
            padding: 0 0.5em;
            margin: 0 1em;
            height: $height-button;
            border: 1px solid transparent;
            transition: border-color 0.2s;
            @include border-radius-smaller;

            &:hover,
            &:focus {
                border-color: $color-border;
            }
        }
    }

    .user-chooser {
        width: 140px;
        height: $height-button;
        margin: 0 1em 0 0.25em;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .main-wrapper {
        display: flex;
        height: calc(100% - #{$height-office-header});
        position: relative;
    }

    .left {
        display: flex;
        flex-direction: column;
        overflow: auto;
        border-right: 1px solid $color-border;
        padding: 0 1em 1em;

        .title {
            font-weight: $bold;
            margin-top: 2em;
        }

        .item {
            display: flex;
            align-items: center;
            padding-top: 1em;
            user-select: none;

            .text {
                flex-grow: 1;
                padding: 0 0.5em;
            }

            &.form,
            &.extra {
                cursor: pointer;

                .far {
                    width: 1em;
                    flex-shrink: 0;

                    &:hover {
                        color: $color-error;
                    }
                }
            }
        }

        .info-label {
            margin-top: 0.25em;
            font-size: 11px;
        }

        .extra {
            color: $color-extra-link;
        }
    }

    .filler-nav {
        border-top: none;
    }

    .canvas {
        height: calc(100% - #{$height-filler-nav});
        overflow-y: scroll;
    }

    .no-items {
        position: relative;
        height: 100%;
        width: 100%;
        background-color: $color-disabled-bg;

        .notice {
            text-align: center;
            position: absolute;
            top: 50px;
            left: 50%;
            width: 50%;
            transform: translateX(-50%);
            font-size: $font-size-larger;

            > div:not(:first-child) {
                margin-top: 1em;
            }
        }
    }

    .error-overlay {
        position: fixed;
        top: $height-office-header;
        left: 0;
        height: calc(100% - #{$height-office-header});
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999;
        background-color: #efefef;
        padding: 2em;

        i {
            font-size: 2.5em;
            margin-right: 30px;
            color: $color-highlight;
        }

        > div {
            max-width: 800px;
        }
    }

    .qr-overlay {
        position: absolute;
        top: 0;
        left: 0;
        height: 100%;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999;
        background-color: $color-modal-blocker;

        > div {
            background-color: #fff;
            padding: 15px;
            @include border-radius-small;
            @include box-shadow-large;
        }

        h4 {
            margin-top: 0;
        }

        .qr-code {
            text-align: center;

            canvas {
                width: 200px;
                margin-bottom: 1em;
            }
        }

        button {
            display: block;
            width: 100%;
        }
    }
</style>